import { MutableRefObject, useCallback } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import {
  TextFieldProps,
  TextField,
  FormControl,
  Box,
  FormLabel,
  FormHelperText,
  Switch,
  makeStyles,
  IconButton,
} from '@material-ui/core';
import { Edit as EditIcon } from '@material-ui/icons';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useFormikContext } from 'formik';

import DetailItem from 'pages/Contatos/components/DetailItems';
import {
  CustomFieldInterface,
  CustomFieldsEnum,
} from 'store/reducers/contatos';

import DocumentTextField from './DocumentTextField';
import MonetaryValueField from './MonetaryValueField';
import NumberValueField from './NumberValueField';

const useStyles = makeStyles(({ typography, spacing, palette }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
  },
  helperText: {
    marginLeft: 0,
  },
  detailItem: {
    '& > .MuiTypography-root': {
      fontSize: typography.pxToRem(14),
      width: 100,
    },

    '&:hover svg': {
      visibility: 'visible',
    },

    '& > div > span': {
      fontSize: typography.pxToRem(14),
    },
  },
  editIcon: {
    width: spacing(2.5),
    height: spacing(2.5),
    fill: palette.text.disabled,
    visibility: 'hidden',
    cursor: 'pointer',
  },
  inputText: {
    '& .MuiInputLabel-root': {
      fontSize: typography.pxToRem(14),
    },

    '& .MuiInputBase-input': {
      fontSize: typography.pxToRem(14),
    },
  },
}));

interface CustomFieldsProps {
  customFields: CustomFieldInterface[];
  editMode?: boolean; // flag que determina se os campos serão renderizados como inputs ou apenas textos
  readonlyFields?: boolean; // se true, não permite edição dos campos, omitindo o botão de edição
  inputRefs?: MutableRefObject<Record<string, HTMLInputElement>>;
  onEnableEditMode?: (value: boolean) => void;
}

export default function CustomFields({
  customFields,
  editMode = true,
  readonlyFields = false,
  inputRefs,
  onEnableEditMode,
}: CustomFieldsProps) {
  const classes = useStyles();
  const formik = useFormikContext<{ [key: string]: string }>();

  const handleEdit = (id: string) => {
    if (onEnableEditMode) onEnableEditMode(true);

    if (!inputRefs) return;

    setTimeout(() => {
      inputRefs.current[id]?.focus();
    }, 0);
  };

  const renderCustomField = useCallback(
    (field: CustomFieldInterface) => {
      if (!field) return null;

      const { id, name, description } = field;

      const commonProps: TextFieldProps = {
        id: String(id),
        name: String(id),
        fullWidth: true,
        label: name,
        inputRef: el => {
          if (inputRefs) inputRefs.current[id] = el as HTMLInputElement;
        }, // para focar o input ao clicar no botão de edição
        variant: 'outlined',
        value: formik.values[id] || '',
        onChange: formik.handleChange,
        error: formik.touched[id] && Boolean(formik.errors[id]),
        helperText:
          formik.touched[id] && formik.errors[id]
            ? String(formik.errors[id])
            : description,
        FormHelperTextProps: { className: classes.helperText },
        classes: {
          root: classes.inputText,
        },
      };

      switch (field.type) {
        case CustomFieldsEnum.CNPJ:
        case CustomFieldsEnum.CPF:
          return (
            <DocumentTextField
              documentType={field.type?.toUpperCase() as 'CPF' | 'CNPJ'}
              {...commonProps}
            />
          );

        case CustomFieldsEnum.SHORT_TEXT:
        case CustomFieldsEnum.EMAIL:
        case CustomFieldsEnum.URL:
          return <TextField {...commonProps} />;

        case CustomFieldsEnum.LONG_TEXT:
          return <TextField multiline minRows={2} {...commonProps} />;

        case CustomFieldsEnum.INTEGER:
          return <NumberValueField {...commonProps} />;

        case CustomFieldsEnum.LOGICAL:
          return (
            <FormControl
              error={formik.touched[id] && Boolean(formik.errors[id])}
            >
              <Box display="flex" alignItems="center" gridGap={8}>
                <Switch
                  id={String(id)}
                  name={String(id)}
                  size="small"
                  checked={Boolean(formik.values[id])}
                  onChange={formik.handleChange}
                  color="primary"
                />
                <FormLabel htmlFor={String(id)}>{name}</FormLabel>
              </Box>
              <FormHelperText>
                {formik.touched[id] && formik.errors[id]
                  ? String(formik.errors[id])
                  : description}
              </FormHelperText>
            </FormControl>
          );

        case CustomFieldsEnum.MONEY:
          return (
            <MonetaryValueField
              id={String(id)}
              name={String(id)}
              fullWidth
              label={name}
              variant="outlined"
              value={formik.values[id]}
              onChange={numericValue => {
                formik.setFieldValue(String(id), numericValue);
              }}
              error={formik.touched[id] && Boolean(formik.errors[id])}
              helperText={
                formik.touched[id] && formik.errors[id]
                  ? String(formik.errors[id])
                  : description
              }
              FormHelperTextProps={{ className: classes.helperText }}
            />
          );

        case CustomFieldsEnum.DATE:
          return (
            <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
              <KeyboardDatePicker
                id={String(id)}
                name={String(id)}
                inputVariant="outlined"
                variant="dialog"
                fullWidth
                cancelLabel="Cancelar"
                format="dd/MM/yyyy"
                margin="none"
                label={name}
                value={
                  typeof formik.values[id] === 'string'
                    ? new Date(`${formik.values[id]}T00:00`)
                    : formik.values[id] || null
                }
                onChange={date => formik.setFieldValue(String(id), date)}
                error={formik.touched[id] && Boolean(formik.errors[id])}
                helperText={
                  formik.touched[id] && formik.errors[id]
                    ? String(formik.errors[id])
                    : description
                }
                FormHelperTextProps={{ className: classes.helperText }}
              />
            </MuiPickersUtilsProvider>
          );

        default:
          return null;
      }
    },
    [classes.helperText, classes.inputText, formik, inputRefs],
  );
  return (
    <Box className={classes.container}>
      {!editMode &&
        customFields.length > 0 &&
        customFields.map(field => {
          if (
            readonlyFields &&
            !field.value &&
            field.type !== CustomFieldsEnum.LOGICAL
          ) {
            return null;
          }

          let displayValue: React.ReactNode =
            field.type === CustomFieldsEnum.LOGICAL ? 'Não' : '-';

          if (field.value != null) {
            switch (field.type) {
              case CustomFieldsEnum.DATE:
                displayValue = format(
                  new Date(`${field.value}T00:00`),
                  'dd/MM/yyyy',
                );
                break;

              case CustomFieldsEnum.LOGICAL:
                displayValue = field.value ? 'Sim' : 'Não';
                break;

              default:
                displayValue = String(field.value);
                break;
            }
          }

          return (
            <DetailItem
              className={classes.detailItem}
              isContactItem
              key={field.id}
              label={field.name}
              value={displayValue}
              endIcon={
                !readonlyFields && (
                  <IconButton size="small" onClick={() => handleEdit(field.id)}>
                    <EditIcon className={classes.editIcon} />
                  </IconButton>
                )
              }
            />
          );
        })}
      {editMode &&
        customFields.length > 0 &&
        customFields.map(field => (
          <Box key={field.id}>{renderCustomField(field)}</Box>
        ))}
    </Box>
  );
}
